import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";

const PITCH_DAY_LABELS = {
  "2026-06-16": "Tuesday, June 16",
  "2026-06-17": "Wednesday, June 17",
  "2026-06-18": "Thursday, June 18",
};

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  city: "",
  source: "",
  reminder_requested: true,
};

export const LS_UNLOCK_KEY = (day) => `bs_ls_unlocked_${day}`;

export function isUnlocked(pitchDay) {
  try {
    return Boolean(localStorage.getItem(LS_UNLOCK_KEY(pitchDay)));
  } catch {
    return false;
  }
}

export function getUnlockedUrl(pitchDay) {
  try {
    return localStorage.getItem(LS_UNLOCK_KEY(pitchDay)) || "";
  } catch {
    return "";
  }
}

export default function LivestreamGateModal({ open, onOpenChange, pitchDay, onUnlocked }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleOpenChange = (next) => {
    if (!next) setForm(EMPTY_FORM);
    onOpenChange(next);
  };

  const handleChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("Please fill in first name, last name, and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/livestream-access", {
        ...form,
        pitch_day: pitchDay,
      });
      try {
        localStorage.setItem(LS_UNLOCK_KEY(pitchDay), data.livestream_url);
      } catch { /* ignore quota errors */ }
      toast.success("You\u2019re in! Opening the livestream and emailing the link.");
      onUnlocked?.(data.livestream_url);
      setForm(EMPTY_FORM);
      onOpenChange(false);
      window.open(data.livestream_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const dayLabel = PITCH_DAY_LABELS[pitchDay] || pitchDay;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="livestream-gate-modal" className="max-w-md bg-white">
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.3em] text-rose-600 font-[Outfit] font-bold">
            Tonight &middot; {dayLabel}
          </div>
          <DialogTitle className="font-[Cormorant_Garamond] text-3xl text-emerald-950 leading-tight">
            Get tonight&apos;s livestream link
          </DialogTitle>
          <DialogDescription className="text-slate-700 font-[Outfit]">
            Drop your info below and we&apos;ll open the livestream + email you the link.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1">
              <Label htmlFor="ls-first" className="text-xs font-[Outfit] font-semibold text-emerald-950">
                First name *
              </Label>
              <Input
                id="ls-first"
                data-testid="ls-first-name"
                value={form.first_name}
                onChange={handleChange("first_name")}
                required
                autoComplete="given-name"
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="ls-last" className="text-xs font-[Outfit] font-semibold text-emerald-950">
                Last name *
              </Label>
              <Input
                id="ls-last"
                data-testid="ls-last-name"
                value={form.last_name}
                onChange={handleChange("last_name")}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="ls-email" className="text-xs font-[Outfit] font-semibold text-emerald-950">
              Email *
            </Label>
            <Input
              id="ls-email"
              data-testid="ls-email"
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              required
              autoComplete="email"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1">
              <Label htmlFor="ls-phone" className="text-xs font-[Outfit] font-semibold text-slate-600">
                Phone (optional)
              </Label>
              <Input
                id="ls-phone"
                data-testid="ls-phone"
                value={form.phone}
                onChange={handleChange("phone")}
                autoComplete="tel"
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="ls-city" className="text-xs font-[Outfit] font-semibold text-slate-600">
                City (optional)
              </Label>
              <Input
                id="ls-city"
                data-testid="ls-city"
                value={form.city}
                onChange={handleChange("city")}
                autoComplete="address-level2"
              />
            </div>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="ls-source" className="text-xs font-[Outfit] font-semibold text-slate-600">
              How did you hear about us? (optional)
            </Label>
            <Input
              id="ls-source"
              data-testid="ls-source"
              value={form.source}
              onChange={handleChange("source")}
              placeholder={"Friend, Instagram, Wix site, email\u2026"}
            />
          </div>

          <div
            data-testid="ls-reminder-row"
            className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 mt-1"
          >
            <Switch
              id="ls-reminder"
              data-testid="ls-reminder-toggle"
              checked={form.reminder_requested}
              onCheckedChange={(v) => setForm((p) => ({ ...p, reminder_requested: Boolean(v) }))}
              className="mt-0.5"
            />
            <Label htmlFor="ls-reminder" className="cursor-pointer flex-1">
              <div className="text-sm font-[Outfit] font-semibold text-emerald-950">
                Email me a reminder 30 min before
              </div>
              <div className="text-xs font-[Outfit] text-slate-600 mt-0.5">
                We&apos;ll send the Zoom link again right before pitches start so it&apos;s top of inbox.
              </div>
            </Label>
          </div>

          <Button
            type="submit"
            data-testid="ls-submit"
            disabled={submitting}
            className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit] font-semibold h-11 mt-1"
          >
            {submitting ? "Sending…" : "Get the Link & Watch Live →"}
          </Button>
          <p className="text-[11px] text-slate-500 font-[Outfit] leading-relaxed">
            We respect your privacy. We&apos;ll only contact you about Business Showers events.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
